// Import JS
import Lenis from 'lenis';
import { CountUp } from 'countup.js';
import Swal from 'sweetalert2';

// Register
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Main Code //
document.addEventListener('DOMContentLoaded', () => {
    ////////////////// Lenis //////////////////
    const lenis = new Lenis({
        duration: 1.5,
        direction: 'vertical',
        gestureDirection: 'vertical',
        smooth: true,
        smoothTouch: false,
        touchMultiplier: 2,
        infinite: false,
    });

    function raf(time) {
        lenis.raf(time);
        ScrollTrigger.update();
        requestAnimationFrame(raf);
    };

    requestAnimationFrame(raf);
    lenis.on('scroll', ScrollTrigger.update);
    window.lenis = lenis;

    ////////////////// GSAP //////////////////
    // Function to create ScrollTrigger for pinning sections
    const createPinTrigger = (trigger, start, end) => {
        ScrollTrigger.create({
            trigger,
            start,
            end,
            pin: true,
            pinSpacing: false,
            scrub: true,
        });
    };

    // Pin sections
    createPinTrigger("#section_hero", "bottom bottom", "bottom");
    createPinTrigger("#section_about", "top top", "top");
    createPinTrigger("#section_about", "bottom bottom", "bottom")
    createPinTrigger("#section_case", "top top", "top");;
    createPinTrigger("#section_case", "bottom bottom", "bottom");
    createPinTrigger("#section_pricing", "top top", "top");
    createPinTrigger("#section_pricing", "bottom bottom", "bottom");
    createPinTrigger("#section_contact", "top top", "top");
    createPinTrigger("#section_contact", "bottom bottom", "bottom");
    createPinTrigger("#section_footer", "top top", "top");

    // GSAP Code Optimization
    function animateHeading(headingSelector, sectionSelector, startOpacity, endOpacity) {
      let lines = gsap.utils.toArray(`${headingSelector} .word`);
      gsap.set(lines, { opacity: startOpacity });

      gsap.to(lines, {
          scrollTrigger: {
              trigger: sectionSelector,
              start: "top center",
              end: "top -top",
              scrub: true,
          },
          opacity: endOpacity,
          stagger: {
              each: 0.05,
              from: "start",
          },
          ease: "power2.in",
      });
    };

    // Animate sections
    animateHeading("#about-heading", "#section_about", 0.6, 1);
    animateHeading("#Inquiry_heading", "#section_pricing", 1, 0.6);
    animateHeading("#pricing_heading", "#section_pricing", 1, 0.6);
    animateHeading("#contact_heading", "#section_contact", 1, 0.6);

    ////////////////// General Code //////////////////
    const manageHeaderScroll = () => {
        const header = document.querySelector('header');
        const navbar = document.querySelector('#navbar');
        const navbarTop = document.querySelector('#navbarTop');
        const navbarBottom = document.querySelector('#navbarBottom');
        
        const headerHeight = header.offsetHeight;
        const scrollTopTrigger = 100;
        let ticking = false;
    
        const headerScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
            if (scrollTop > scrollTopTrigger / 2) {
                // Reduce width and move to bottom
                const translateYValue = `${window.innerHeight - headerHeight}px`; // 16px margin at the bottom
                navbar.classList.remove("lg:max-w-full");
                navbar.classList.add("lg:max-w-[600px]");
                header.style.transform = `translateY(${translateYValue})`;
    
                // Hide top navbar, show bottom
                navbarTop.classList.add('xl:opacity-0', 'xl:pointer-events-none', 'xl:-z-10');
                navbarTop.classList.remove('xl:opacity-100', 'xl:pointer-events-auto');
                navbarBottom.classList.remove('xl:opacity-0', 'xl:pointer-events-none', 'xl:-z-10');
            } else {
                document.querySelector('#sidebar').classList.add('-translate-x-full');

                // Reset width and position
                navbar.classList.remove("lg:max-w-[600px]");
                navbar.classList.add("lg:max-w-full");
                header.style.transform = 'translateY(0)'; // Reset position
    
                // Show top navbar, hide bottom
                navbarTop.classList.remove('xl:opacity-0', 'xl:pointer-events-none', 'xl:-z-10');
                navbarTop.classList.add('xl:opacity-100', 'xl:pointer-events-auto');
                navbarBottom.classList.add('xl:opacity-0', 'xl:pointer-events-none', 'xl:-z-10');
            }
        };
    
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    headerScroll();
                    ticking = false; // Allow the next scroll event to be processed
                });
                ticking = true; // Prevent additional calls until the current frame is processed
            }
        };

        // Set transitions for smooth effects
        header.style.transition = 'transform 0.5s ease';
        navbar.style.transition = 'max-width 0.5s ease';
    
        // Listen for scroll event
        window.addEventListener('scroll', onScroll);

        // Function to calculate the Y position of an element relative to the document
        const getElementYPosition = (element) => {
            if (!element) return 0; // Return 0 if the element is not found
            const rect = element.getBoundingClientRect();
            return rect.top + window.pageYOffset; // Calculate the Y position relative to the document
        };

        // ScrollToTarget function using Lenis
        const scrollToTarget = (target, duration) => {
            const element = document.querySelector(target);
            if (!element) {
                console.error("Element not found:", target); // Log if the element is not found
                return;
            }

            const targetY = getElementYPosition(element); // Get consistent Y position
            lenis.scrollTo(targetY, {
                duration: duration,
                easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            });

            // console.log("Scrolling to:", targetY); // Log the targetY for debugging
        };

        // Attach event listeners to all .scroll-btn elements
        const buttons = document.querySelectorAll(".scroll-btn");

        buttons.forEach((btn) => {
            btn.addEventListener("click", (e) => {
                e.preventDefault();

                // Priority 1: use data-target
                let target = btn.getAttribute("data-target");

                // Fallback: use href (like <a href="#section">)
                if (!target && btn.tagName.toLowerCase() === "a") {
                    target = btn.getAttribute("href");
                    document.querySelector('#sidebar').classList.add('-translate-x-full');
                }

                if (target && target.startsWith("#")) {
                    scrollToTarget(target, 2);
                    document.querySelector('#sidebar').classList.add('-translate-x-full');
                } else {
                    console.warn("No valid target found on", btn);
                }
            });
        });
    };
    
    manageHeaderScroll();

    const manageCarousel = () => {
        // Carousel active states
        let carouselActiveStates = {};
        let carouselActiveCategory = {};
    
        // Define the categories and their respective image counts
        const carouselCategories = [
            [
                { id: 0, name: "Website Design", imageCount: 3, },
                { id: 1, name: "Mobile App", imageCount: 3 },
                { id: 2, name: "Dashboard", imageCount: 2 },
            ],
            [
                { id: 0, name: "Brand", imageCount: 3 },
                { id: 1, name: "Pitch Deck", imageCount: 3  },
                { id: 2,  name: "Social Media", imageCount: 3 },
                { id: 3, name: "App Screenshot", imageCount: 3 },
            ],
            [
                { id: 0, name: "Flat Illustration", imageCount: 3 },
                { id: 1, name: "Isometric Illustration", imageCount: 3  },
            ],
            [
                { id: 0, name: "3D Animation", imageCount: 3 },
                { id: 1, name: "3D Modeling", imageCount: 3 },
                { id: 2, name: "Motion Graphic", imageCount: 2 },
            ],
            // Fix Later
            [
                { id: 0, name: "Brand", imageCount: 3 },
                { id: 1, name: "Pitch Deck", imageCount: 3  },
                { id: 2,  name: "Social Media", imageCount: 3 },
                { id: 3, name: "App Screenshot", imageCount: 3 },
                { id: 4, name: "App Screenshot", imageCount: 3 },
            ],
            [
                { id: 0, name: "Brand", imageCount: 3 },
                { id: 1, name: "Pitch Deck", imageCount: 3  },
                { id: 2,  name: "Social Media", imageCount: 3 },
                { id: 3, name: "App Screenshot", imageCount: 3 },
            ],
        ];

        // Helper to update the carousel transform based on width and index
        function updateCarouselPosition(carouselIndex, carouselContainer) {
            const container = document.querySelector(`.${carouselContainer}`);
            
            if (container) {
                const firstItem = container.querySelector('.carousel-items');
        
                // Use getBoundingClientRect() to get a more accurate width
                const itemWidth = firstItem ? firstItem.getBoundingClientRect().width : 0;
                
                // Set the transform without a fixed offset
                container.style.transform = `translateX(${-itemWidth * carouselIndex}px)`;
            }
        }
    
        // Helper to toggle classes
        function toggleClass(elements, activeIndex, activeClass, inactiveClass) {
            elements.forEach((element, index) => {
                element.classList.toggle(activeClass, index == activeIndex);
                element.classList.toggle(inactiveClass, index != activeIndex);
            });
        };
    
        // Attach hover events to carousel sections
        function attachCarouselHoverEvents(carouselContainer, targetClass) {
            document.querySelectorAll(`.${targetClass}`).forEach((target) => {
                target.addEventListener('mouseenter', () => {
                    const value = parseInt(target.value, 10);
                    let computedValue = value;
            
                    if (carouselActiveCategory[carouselContainer] !== undefined) {
                        computedValue = value + carouselActiveCategory[carouselContainer];
                    }
                    updateCarouselPosition(computedValue, carouselContainer)
                });
                target.addEventListener('mouseleave', () => {
                    updateCarouselPosition(carouselActiveStates[carouselContainer] || 0, carouselContainer);
                });
            });
        };
    
        // Main function to handle carousel radio selection and styling
        function attachCarouselActiveRadio(carouselContainer, radioName) {
            const radios = document.querySelectorAll(`input[name="${radioName}"]`);
            const divs = Array.from(radios).map(radio => radio.nextElementSibling); // Get sibling divs next to each radio input
    
            radios.forEach(radio => {
                radio.addEventListener('change', () => {
                    const value = parseInt(radio.value, 10);
                    let computedValue = value;
            
                    if (carouselActiveCategory[carouselContainer] !== undefined) {
                        computedValue = value + carouselActiveCategory[carouselContainer];
                    }
            
                    carouselActiveStates[carouselContainer] = computedValue;
                    updateCarouselPosition(computedValue, carouselContainer);
    
                    if (radio.checked) {
                        toggleClass(divs, value, 'bg-primPurple', 'bg-[#323232]');
                    }
                });
            });
    
            const firstRadioValue = parseInt(radios[0].value, 10);
            updateCarouselPosition(firstRadioValue, carouselContainer);
        }

        function attachCarouselSwipeRadio(carouselContainer, carouselSwipe, radioName) {
            const container = document.querySelector(`.${carouselContainer}`);
            const carouselItems = container.querySelectorAll('.carousel-items');
            const swipe = document.querySelector(`.${carouselSwipe}`);
            const radios = document.querySelectorAll(`input[name="${radioName}"]`);
            const divs = Array.from(radios).map(radio => radio.nextElementSibling);
            let isDragging = false;
            let touchStartX = 0;
            let touchEndX = 0;

            swipe.addEventListener("touchstart", (e) => {
                touchStartX = e.touches[0].clientX;
            });

            swipe.addEventListener("touchend", (e) => {
                touchEndX = e.changedTouches[0].clientX;
                handleSwipe();
            });

            swipe.addEventListener("mousedown", (e) => {
                isDragging = true;
                touchStartX = e.clientX;
            });

            swipe.addEventListener("mouseup", (e) => {
                if (!isDragging) return;
                isDragging = false;
                touchEndX = e.clientX;

                handleSwipe();
            });

            function handleSwipe() {
                const checkedRadio = document.querySelector(`input[name="${radioName}"]:checked`);
                let currentIndex = parseInt(checkedRadio.value);
                let fallofValue = carouselActiveCategory[carouselContainer] ?? 0;
                let diff = touchEndX - touchStartX;
                let direction = diff < 0 ? 1 : -1;
                let nextIndex = currentIndex + direction;
                let nextComputedValue = nextIndex + fallofValue;

                if (Math.abs(diff) < 50) return;

                if (direction > 0 && currentIndex < radios.length - 1 && nextComputedValue < carouselItems.length) {
                    currentIndex = nextIndex;
                } else if (direction < 0 && currentIndex > 0) {
                    currentIndex = nextIndex;
                } else {
                    return;
                }

                const computedValue = currentIndex + fallofValue;
                carouselActiveStates[carouselContainer] = computedValue;
                toggleClass(divs, currentIndex, 'bg-primPurple', 'bg-[#323232]');
                updateCarouselPosition(computedValue, carouselContainer);
                radios[currentIndex].checked = true;
                console.log("Computed:", computedValue);
            }
        }
    
        // Attach toggle and radio events
        function attachToggleEvents(carouselContainer, radioName, hoverButton, toggleName, toggleHeader, toggleDesc, categoryIndex) {
            const radios = document.querySelectorAll(`input[name="${radioName}"]`);
            const radiosdivs = Array.from(radios).map(radio => radio.nextElementSibling);
            const hoverButtons = document.querySelectorAll(`.${hoverButton}`);
            const hoverDiv = document.querySelector(`.${hoverButton}`).closest("div");
            const toggleButtons = document.querySelectorAll(`input[name="${toggleName}"]`);
            const toggledivs = Array.from(toggleButtons).map(radio => radio.nextElementSibling);
            const toggleHead = document.querySelectorAll(`.${toggleHeader}`);
            const toggleText = document.querySelectorAll(`.${toggleDesc}`);
    
            toggleButtons.forEach((toggle) => {
                toggle.addEventListener('change', () => {
                    const value = parseInt(toggle.value, 10);
                    let computedValue = value * 3;
                    carouselActiveCategory[carouselContainer] = computedValue;
                    carouselActiveStates[carouselContainer] = computedValue;
                    updateCarouselPosition(computedValue, carouselContainer);

                    toggleClass(toggledivs, value, 'bg-white', 'bg-primBlack');
                    toggleClass(toggledivs, value, 'group-hover:bg-primYellow/100', 'group-hover:bg-white/60');
                    toggleClass(toggledivs, value, 'text-primBlack', 'text-white');
                    toggleClass(toggleHead, value, 'translate-y-0', '-translate-y-full');
                    toggleClass(toggleHead, value, 'relative', 'absolute');
                    toggleClass(toggleHead, value, 'opacity-100', 'opacity-0');
                    toggleClass(toggleText, value, 'translate-x-0', 'translate-x-full');
                    toggleClass(toggleText, value, 'relative', 'absolute');
                    toggleClass(toggleText, value, 'opacity-100', 'opacity-0');
                    
                    const category = carouselCategories[categoryIndex].find(cat => cat.id === value);
                    const imageCount = category ? category.imageCount : 0;

                    const radioButton = radios[0];
                    if (radioButton) {
                        toggleClass(radiosdivs, 0, 'bg-primPurple', 'bg-[#323232]');
                        if (imageCount == 3) {
                            radiosdivs[2].classList.remove("opacity-0", "hidden");
                            radios[2].classList.remove("pointer-events-none");
                            radiosdivs[1].classList.remove("opacity-0", "hidden");
                            radios[1].classList.remove("pointer-events-none");
                            radiosdivs[0].classList.remove("opacity-0", "hidden");
                            radios[0].classList.remove("pointer-events-none");
                        } else if(imageCount == 2){
                            radiosdivs[2].classList.add("opacity-0", "hidden");
                            radios[2].classList.add("pointer-events-none");
                            radiosdivs[1].classList.remove("opacity-0", "hidden");
                            radios[1].classList.remove("pointer-events-none");
                            radiosdivs[0].classList.remove("opacity-0", "hidden");
                            radios[0].classList.remove("pointer-events-none");
                        } else if (imageCount == 1) {
                            radiosdivs[2].classList.add("opacity-0", "hidden");
                            radios[2].classList.add("pointer-events-none");
                            radiosdivs[1].classList.add("opacity-0", "hidden");
                            radios[1].classList.add("pointer-events-none");
                            radiosdivs[0].classList.remove("opacity-0", "hidden");
                            radios[0].classList.remove("pointer-events-none");
                        } else {
                            radiosdivs[2].classList.add("opacity-0", "hidden");
                            radios[2].classList.add("pointer-events-none");
                            radiosdivs[1].classList.add("opacity-0", "hidden");
                            radios[1].classList.add("pointer-events-none");
                            radiosdivs[0].classList.add("opacity-0", "hidden");
                            radios[0].classList.add("pointer-events-none");
                        }
                        radioButton.checked = true;
                    };

                    if(hoverButtons){
                        if (imageCount == 3) {
                            hoverButtons[2].classList.remove("opacity-0", "hidden");
                            hoverButtons[1].classList.remove("opacity-0", "hidden");
                            hoverButtons[0].classList.remove("opacity-0", "hidden");
                            hoverDiv.classList.add("grid-cols-3");
                            hoverDiv.classList.remove("grid-cols-2", "grid-cols-1");
                        } else if(imageCount == 2){
                            hoverButtons[2].classList.add("opacity-0", "hidden");
                            hoverButtons[1].classList.remove("opacity-0", "hidden");
                            hoverButtons[0].classList.remove("opacity-0", "hidden");
                            hoverDiv.classList.add("grid-cols-2");
                            hoverDiv.classList.remove("grid-cols-3", "grid-cols-1");
                        } else if (imageCount == 1) {
                            hoverButtons[2].classList.add("opacity-0", "hidden");
                            hoverButtons[1].classList.add("opacity-0", "hidden");
                            hoverButtons[0].classList.remove("opacity-0", "hidden");
                            hoverDiv.classList.add("grid-cols-1");
                            hoverDiv.classList.remove("grid-cols-3", "grid-cols-2");
                        } else {
                            hoverButtons[2].classList.add("opacity-0", "hidden");
                            hoverButtons[1].classList.add("opacity-0", "hidden");
                            hoverButtons[0].classList.add("opacity-0", "hidden");
                            hoverDiv.classList.remove("grid-cols-3", "grid-cols-2", "grid-cols-1");
                        }
                    };
                });
            });

            const radioButton = radios[0];
            if (radioButton) {
                toggleClass(radiosdivs, 0, 'bg-primPurple', 'bg-[#323232]');
                radioButton.checked = true;
            };

            const toggleButton = toggleButtons[0];
            if(toggleButton) {
                toggleClass(toggleText, 0, 'translate-x-0', 'translate-x-full');
                toggleClass(toggleText, 0, 'relative', 'absolute');
                toggleClass(toggleText, 0, 'opacity-100', 'opacity-0');
    
                toggleClass(toggleHead, 0, 'translate-y-0', '-translate-y-full');
                toggleClass(toggleHead, 0, 'relative', 'absolute');
                toggleClass(toggleHead, 0, 'opacity-100', 'opacity-0');
    
                toggleClass(toggledivs, 0, 'bg-white', 'bg-primBlack');
                toggleClass(toggledivs, 0, 'group-hover:bg-primYellow/100', 'group-hover:bg-white/60');
                toggleClass(toggledivs, 0, 'text-primBlack', 'text-white');
                toggleButton.checked = true;
            };
        };
    
        // Initialize carousels and event listeners
        attachCarouselHoverEvents("case-container-webDesign", "case-hover-webDesign");
        attachCarouselActiveRadio("case-container-webDesign", "case-active-webDesign");
        attachCarouselSwipeRadio("case-container-webDesign", "case-swipe-webDesign", "case-active-webDesign")
        attachToggleEvents("case-container-webDesign", "case-active-webDesign", "case-hover-webDesign", "case-category-webDesign", "case-header-webDesign", "case-description-webDesign", 0);

        attachCarouselHoverEvents("case-container-branding", "case-hover-branding");
        attachCarouselActiveRadio("case-container-branding", "case-active-branding");
        attachCarouselSwipeRadio("case-container-branding", "case-swipe-branding", "case-active-branding")
        attachToggleEvents("case-container-branding", "case-active-branding", "case-hover-branding", "case-category-branding", "case-header-branding", "case-description-branding", 1);

        attachCarouselHoverEvents("case-container-illustration", "case-hover-illustration");
        attachCarouselActiveRadio("case-container-illustration", "case-active-illustration");
        attachCarouselSwipeRadio("case-container-illustration", "case-swipe-illustration", "case-active-illustration")
        attachToggleEvents("case-container-illustration", "case-active-illustration", "case-hover-illustration", "case-category-illustration", "case-header-illustration", "case-description-illustration", 2);
    
        attachCarouselHoverEvents("case-container-3DArt", "case-hover-3DArt");
        attachCarouselActiveRadio("case-container-3DArt", "case-active-3DArt");
        attachCarouselSwipeRadio("case-container-3DArt", "case-swipe-3DArt", "case-active-3DArt")
        attachToggleEvents("case-container-3DArt", "case-active-3DArt", "case-hover-3DArt", "case-category-3DArt", "case-header-3DArt", "case-description-3DArt", 3);

        attachCarouselHoverEvents("case-container-Development", "case-hover-Development");
        attachCarouselActiveRadio("case-container-Development", "case-active-Development");
        attachCarouselSwipeRadio("case-container-Development", "case-swipe-Development", "case-active-Development")
        attachToggleEvents("case-container-Development", "case-active-Development", "case-hover-Development", "case-category-Development", "case-header-Development", "case-description-Development", 4);

        attachCarouselHoverEvents("case-container-Merchandise", "case-hover-Merchandise");
        attachCarouselActiveRadio("case-container-Merchandise", "case-active-Merchandise");
        attachCarouselSwipeRadio("case-container-Merchandise", "case-swipe-Merchandise", "case-active-Merchandise")
        attachToggleEvents("case-container-Merchandise", "case-active-Merchandise", "case-hover-Merchandise", "case-category-Merchandise", "case-header-Merchandise", "case-description-Merchandise", 5);

        let resizeTimeout;
        window.addEventListener('resize', function() {
            // Clear any previous timeouts to avoid multiple triggers
            clearTimeout(resizeTimeout);

            // Set a timeout to delay the execution of the function by 1 second (1000 ms)
            resizeTimeout = setTimeout(function() {
                updateCarouselPosition(carouselActiveStates["case-container-webDesign"], "case-container-webDesign");
                updateCarouselPosition(carouselActiveStates["case-container-branding"], "case-container-branding");
                updateCarouselPosition(carouselActiveStates["case-container-illustration"], "case-container-illustration");
            }, 1000);  // Delay of 1000ms (1 second)
        });
    };

    manageCarousel();

    const manageService = () => {
        // Apply background color to checkboxes
        const applyBackgroundColor = (checkbox) => {
            const div = checkbox.nextElementSibling;
            if (checkbox.checked) {
                div.classList.replace('bg-white', 'bg-primYellow');
                div.classList.replace('text-[#737373]', 'text-black');
                div.classList.replace('border-[#404040]', 'border-primYellow');
                div.classList.replace('hover:bg-primBlack/60', 'hover:bg-black');
            } else {
                div.classList.replace('bg-primYellow', 'bg-white');
                div.classList.replace('text-black', 'text-[#737373]');
                div.classList.replace('border-primYellow', 'border-[#404040]');
                div.classList.replace('hover:bg-black', 'hover:bg-primBlack/60');
            }
        };
    
        // Initialize checkbox backgrounds and add change event listeners
        document.querySelectorAll('.InquiryServiceCheckbox').forEach(checkbox => {
            applyBackgroundColor(checkbox);
            checkbox.addEventListener('change', () => applyBackgroundColor(checkbox));
        });
    
        // Check input value and adjust label
        const checkInputValue = (id, label) => {
            const input = document.getElementById(id);
            const inputLabel = document.getElementById(label);
            if (input && input.value) {
                inputLabel.classList.replace('text-lg', 'text-xs');
                inputLabel.classList.add('-translate-y-full');
            } else {
                inputLabel.classList.replace('text-xs', 'text-lg');
                inputLabel.classList.remove('-translate-y-full');
            }
        };
    
        // Attach input event listeners for labels
        ['name', 'email', 'description'].forEach(id => {
            document.getElementById(id)?.addEventListener('change', () => checkInputValue(id, `${id}-label`));
            checkInputValue(id, `${id}-label`);
        });
    
        // Auto-expand textareas based on input
        const tx = document.getElementsByTagName("textarea");
        for (let i = 0; i < tx.length; i++) {
            const lineHeight = parseInt(window.getComputedStyle(tx[i]).lineHeight, 10);
            const maxHeight = lineHeight * 4; // Maximum height for 6 lines
    
            // Set initial style on page load
            tx[i].setAttribute("style", "height:" + (tx[i].scrollHeight) + "px;overflow-y:hidden;");
    
            // Ensure scrollbar is shown if content exceeds maxHeight on load
            if (tx[i].scrollHeight > maxHeight) {
                tx[i].style.overflowY = "scroll";
                tx[i].style.height = maxHeight + "px"; // Limit height to maxHeight
            }
    
            // Adjust height dynamically on input
            tx[i].addEventListener("input", function () {
                this.style.height = 0; // Reset the height to auto-grow
                const newHeight = Math.min(this.scrollHeight, maxHeight); // Limit height to maxHeight
                this.style.height = newHeight + "px";
    
                // Show scrollbar if the content exceeds 6 lines
                if (this.scrollHeight > maxHeight) {
                    this.style.overflowY = "scroll";
                } else {
                    this.style.overflowY = "hidden";
                }
            });
        }
    
        // Toggle service section visibility
        const toggleSection = (targetId, show) => {
            const target = document.querySelector(targetId);
            const targetAbsolute = document.querySelector('#InquiryMenu');
            if (target) {
                if (show) {
                    target.classList.remove('translate-x-full');
                    target.classList.remove('opacity-0');
                    target.classList.remove('pointer-events-none');
                    if (target === targetAbsolute) {
                        targetAbsolute.classList.remove('h-0', 'overflow-hidden');
                    }
                } else {
                    target.classList.add('translate-x-full');
                    target.classList.add('opacity-0');
                    target.classList.add('pointer-events-none');
                    if (target === targetAbsolute) {
                        setTimeout(() => targetAbsolute.classList.add('h-0', 'overflow-hidden'), 300);
                    }
                }
            }
        };
    
        const checkRadioAndToggle = () => {
            // Loop through all the radio buttons
            document.querySelectorAll('.radio-pricing').forEach((radio) => {
                radio.addEventListener('change', (event) => {
                    // Remove active classes from all elements
                    document.querySelectorAll('.price-type').forEach((type) => {
                        type.classList.remove('text-black');
                        type.classList.add('text-[#737373]');
                    });
                    document.querySelectorAll('.price-loadbar .absolute').forEach((bar) => {
                        bar.classList.add('w-0');
                        bar.classList.remove('w-full');
                    });
        
                    // Add active classes to the selected radio's related elements
                    const label = event.target.closest('label');  // Find the closest label for the checked radio
                    label.querySelector('.price-type').classList.remove('text-[#737373]');
                    label.querySelector('.price-type').classList.add('text-black');
                    label.querySelector('.price-loadbar .absolute').classList.remove('w-0');
                    label.querySelector('.price-loadbar .absolute').classList.add('w-full');
        
                    // Toggle visibility of service sections based on radio selection
                    if (radio.value === 'pricing') {
                        toggleSection('#PricingMenu', true);
                        toggleSection('#InquiryMenu', false);
                    } else if (radio.value === 'inquiry') {
                        toggleSection('#PricingMenu', false);
                        toggleSection('#InquiryMenu', true);
                    }
                });
            });
        };
    
        // Initialize radio styles on page load
        const initializeRadioStyles = () => {
            const defaultRadio = document.querySelector('input[name="pricing-radio"]:checked');
            if (defaultRadio) {
                const event = new Event('change', { bubbles: true });
                defaultRadio.dispatchEvent(event);
            }
        };
    
        // Initialize inquiry price options based on radio button selection
        const radioButtons = document.querySelectorAll('input[name="InquiryPrice"]');
        const handleRadioChange = (event) => {
            document.querySelectorAll('.price-option').forEach(div => {
                div.classList.replace('bg-primYellow', 'bg-white');
                div.classList.replace('text-black', 'text-[#737373]');
                div.classList.replace('border-primYellow', 'border-[#404040]');
                div.classList.replace('hover:bg-black', 'hover:bg-primBlack/60');
            });
    
            const selectedDiv = event.target.nextElementSibling;
            selectedDiv.classList.replace('bg-white', 'bg-primYellow');
            selectedDiv.classList.replace('text-[#737373]', 'text-black');
            selectedDiv.classList.replace('border-[#404040]', 'border-primYellow');
            selectedDiv.classList.replace('hover:bg-primBlack/60', 'hover:bg-black');
            
        };
    
        radioButtons.forEach(radio => radio.addEventListener('change', handleRadioChange));
        const checkedRadio = document.querySelector('input[name="InquiryPrice"]:checked');
        if (checkedRadio) handleRadioChange({ target: checkedRadio });
    
        // Store the current form load time
        const createFormDate = () => {
            const now = new Date();
            const day = String(now.getDate()).padStart(2, '0');
            const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
            const year = now.getFullYear();

            const formattedDate = `${day}/${month}/${year}`;
            document.getElementById('form_load_time').value = formattedDate;
        };
    
        // Initialize functions
        checkRadioAndToggle();
        initializeRadioStyles();
        createFormDate();
    };
    
    // Call the manageService function to activate everything
    manageService();

    const manageCarouselPricing = () => {
        const container = document.querySelector('.drag-pricing');
        const items = container.querySelectorAll('.carousel-price');
        const parent = container.parentElement;
        const progressBar = document.querySelector('.progress-bar');
        const progressTrack = document.querySelector('.drag-progress');

        let currentIndex = 0;
        const totalItems = items.length;
        let itemWidth = items[0]?.offsetWidth || 0;

        function updateItemWidth() {
            itemWidth = items[0]?.offsetWidth || 0;
        }

        function updateProgressBar() {
            const ratio = currentIndex / (totalItems - 1);
            const maxScroll = progressTrack.offsetWidth - progressBar.offsetWidth;
            progressBar.style.left = `${ratio * maxScroll}px`;
        }

        function goToIndex(index) {
            const width = window.innerWidth;
            currentIndex = Math.max(0, Math.min(index, totalItems - 1));
            let offset;

            if (width < 1280) {
                offset = -itemWidth * currentIndex;
            } else {
                offset = 0;
            }

            gsap.to(container, {
                x: offset,
                duration: 0.4,
                ease: "power2.out",
                onUpdate: updateProgressBar
            });
        }

        // TOUCH and MOUSE DRAG support
        function enableDragging() {
            let startX = 0;
            let deltaX = 0;
            let isDragging = false;
            let isTouch = false;

            const onStart = (e) => {
                isDragging = true;
                isTouch = e.type === "touchstart";
                startX = isTouch ? e.touches[0].clientX : e.clientX;
                container.style.cursor = 'grabbing';
            };

            const onMove = (e) => {
                if (!isDragging) return;
                const currentX = isTouch ? e.touches[0].clientX : e.clientX;
                deltaX = currentX - startX;
            };

            const onEnd = () => {
                const width = window.innerWidth;

                if (!isDragging) return;
                isDragging = false;
                container.style.cursor = '';

                if(width < 1280){
                    if (Math.abs(deltaX) > 50) {
                        const direction = deltaX > 0 ? -1 : 1;
                        goToIndex(currentIndex + direction);
                    } else {
                        goToIndex(currentIndex); // Snap back if not enough drag
                    }
                }

                deltaX = 0;
            };

            container.addEventListener('touchstart', onStart);
            container.addEventListener('touchmove', onMove);
            container.addEventListener('touchend', onEnd);

            container.addEventListener('mousedown', onStart);
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onEnd);
        }

        function handleResize() {
            updateItemWidth();
            goToIndex(currentIndex);
        }

        window.addEventListener('resize', handleResize);

        updateItemWidth();
        enableDragging();
        goToIndex(0);
    };

    manageCarouselPricing();

    function copyToClipboard(link) {
      // Create a temporary input element
      var tempInput = document.createElement("input");
      tempInput.value = link;
      document.body.appendChild(tempInput);
  
      // Select and copy the text inside the input element
      tempInput.select();
      document.execCommand("copy");
  
      // Remove the temporary input element
      document.body.removeChild(tempInput);
  
      // Provide feedback to the user (optional)
      // alert("Link copied to clipboard: " + link);
    }

    document.getElementById('emailClipboard').addEventListener("click", () => {
      copyToClipboard("info@nozaracy.com");
      Swal.fire({
        icon: 'success',
        title: 'Copied! You can now paste the email.',
        toast: true,
        position: 'top',
        timer: 3000,
        showConfirmButton: false
      });
    })

    document.querySelectorAll('.clipboard-btn').forEach(function(button) {
        button.addEventListener('click', function() {
            var link = this.getAttribute('data-link'); // Or use another method to get the value
            if (link) {
                copyToClipboard(link);
                Swal.fire({
                    icon: 'success',
                    title: 'Copied! You can now paste the data.',
                    toast: true,
                    position: 'top',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        });
    });

    document.getElementById('emailClipboard2').addEventListener("click", () => {
        copyToClipboard("info@nozaracy.com");
        Swal.fire({
          icon: 'success',
          title: 'Copied! You can now paste the email.',
          toast: true,
          position: 'top',
          timer: 3000,
          showConfirmButton: false
        });
      })

    const manageMousePopUp = () => {
        let isDesktop = window.innerWidth > 768;
    
        function setupHoverEffect(hoverClass, pictureClass, followBoxId) {
            const mouseHover = document.querySelectorAll(hoverClass);
            const mousePicture = document.querySelectorAll(pictureClass);
            const followBox = document.getElementById(followBoxId);
        
            mouseHover.forEach((link, index) => {
                link.addEventListener('mouseenter', () => {
                    if (isDesktop) {
                        followBox.classList.remove('scale-0');
                        mousePicture[index].classList.remove('opacity-0');
                        mouseHover[index].classList.add('cursor-none');
                    }
                });
        
                link.addEventListener('mouseleave', () => {
                    if (isDesktop) {
                        followBox.classList.add('scale-0');
                        mousePicture[index].classList.add('opacity-0');
                        mouseHover[index].classList.remove('cursor-none');
                    }
                });
            });
        }
    
        function updateFollowBoxPosition(e) {
            if (isDesktop) {
                const followBox = document.getElementById('follow-container');
                const boxWidth = followBox.offsetWidth / 2;
                const boxHeight = followBox.offsetHeight / 2;
                followBox.style.transform = `translate(${e.clientX - boxWidth}px, ${e.clientY - boxHeight}px)`;
            }
        }
    
        // Add or remove event listeners based on screen size
        function checkScreenSize() {
            isDesktop = window.innerWidth > 768;
    
            if (isDesktop) {
                document.addEventListener('mousemove', updateFollowBoxPosition);
                document.addEventListener('scroll', updateFollowBoxPosition);
    
                setupHoverEffect('.section1-mouse-hover', '.section1-mouse-picture', 'follow-box');
                // setupHoverEffect('.section3-mouse-hover', '.section3-mouse-picture', 'follow-box');
                setupHoverEffect('.section5-schedule-hover', '.section5-schedule-picture', 'follow-schedule-box');
                setupHoverEffect('.section5-email-hover', '.section5-email-picture', 'follow-email-box');
                setupHoverEffect('.section5-message-hover', '.section5-message-picture', 'follow-message-box');
                setupHoverEffect('.section6-comment-hover', '.section6-comment-picture', 'follow-comment-box');
            } else {
                // Clean up if resizing to mobile
                document.removeEventListener('mousemove', updateFollowBoxPosition);
                document.removeEventListener('scroll', updateFollowBoxPosition);
            }
        }
    
        // Initialize when the page loads
        checkScreenSize();
    
        // Listen for screen resize and adjust behavior
        window.addEventListener('resize', checkScreenSize);
    };
    manageMousePopUp();
});

// Handle Video
window.addEventListener('load', () => {
  //// Set up Video Autoplay //// 
  const manageVideoPlayback = (selector) => {
    document.querySelectorAll(selector).forEach(video => {
        if (!video) return;
        
        // Ensure the video is muted to increase chances of autoplay
        video.autoplay = true;
        video.muted = true;

        let isPlaying = false;

        // Function to attempt to play the video
        const attemptPlay = () => {
            if (!isPlaying) {
                video.play().then(() => {
                    isPlaying = true;
                }).catch(() => {
                    console.log('Autoplay blocked. Waiting for user interaction.');
                });
            }
        };

        const pauseVideo = () => {
            if (isPlaying) {
                video.pause();
                isPlaying = false;
            }
        };

        // Intersection Observer to control video playback based on visibility
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                entry.isIntersecting ? attemptPlay() : pauseVideo();
            });
        }, { threshold: 0.5 });

        // Initialize video management
        observer.observe(video);
        attemptPlay();

        // Add a user interaction listener to play the video on click or touch
        document.addEventListener('click', attemptPlay);
        document.addEventListener('touchstart', attemptPlay);
    });
  };

  // Call the function to manage video playback
  manageVideoPlayback("#background-video");
  manageVideoPlayback('.lazy-video');

  //// Count Up Animation ////
  var countUp = new CountUp('gnr1', 32, {
    duration: 3,
    useEasing: true,
  });
  var countUp2 = new CountUp('gnr2', 92, {
    duration: 3,
    useEasing: true,
  });
  var countUp3 = new CountUp('gnr3', 15, {
    duration: 3,
    useEasing: true,
  });
  var countUp4 = new CountUp('gnr4', 4.5, {
    duration: 3,
    decimalPlaces: 1,
    useEasing: true,
  });

  // Observe Count Up
  function observeNumbers(selector, callback) {
    const elements = document.querySelectorAll(selector);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            callback();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 1 }
    );
    elements.forEach(el => observer.observe(el));
  }

  // Initialize observers
  observeNumbers("#gnr1", () => {
    countUp.start();
    countUp2.start();
  });
  observeNumbers("#gnr3", () => {
    countUp3.start();
    countUp4.start();
  });
});